<template>
  <base-dialog
    :is-open="isOpen"
    max-width="600"
    @close="close"
  >
    <template #dialog-title>
      {{ existingConfig ? 'Spendenbescheinigung bearbeiten' : 'Spendenbescheinigung konfigurieren' }}
    </template>
    <template #dialog-text>
      <v-row>
        <v-col
          cols="12"
          md="6"
        >
          <v-text-field
            v-model="form.min_amount_donation"
            label="Mindestbetrag Spende"
            type="number"
            min="0"
            outlined
            suffix="€"
            dense
            :rules="amountRules"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-text-field
            v-model="form.min_amount_collection"
            label="Mindestbetrag Kollekte"
            type="number"
            min="0"
            outlined
            suffix="€"
            dense
            :rules="amountRules"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-text-field
            v-model="form.valid_from"
            label="Gültig ab"
            type="date"
            outlined
            dense
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-text-field
            v-model="form.valid_notafter"
            label="Gültig bis (optional)"
            type="date"
            outlined
            dense
            clearable
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="form.tax_exempt_purpose"
            label="Gemeinnütziger Zweck"
            outlined
            dense
          />
        </v-col>
        <v-col cols="12">
          <div class="text-subtitle-2 mb-1">Vorlagenset</div>
          <v-radio-group
            v-model="templateSet"
            row
            hide-details
            class="mt-0"
          >
            <v-radio
              v-for="set in templateSets"
              :key="set.value"
              :label="set.label"
              :value="set.value"
              :disabled="!set.complete"
            />
          </v-radio-group>
          <div
            v-if="hasExistingTemplates && !templateSet"
            class="text-caption mt-1"
          >
            Individuelle Vorlagen hinterlegt – ohne Auswahl bleiben diese erhalten.
          </div>
          <div
            v-if="!loadingTemplates && templateSets.some(set => !set.complete)"
            class="text-caption mt-1"
            :class="{ 'error--text': !templateSets.some(set => set.complete) }"
          >
            <div
              v-for="set in templateSets.filter(set => !set.complete)"
              :key="set.value"
            >
              {{ set.label }}: {{ set.missing.map(type => TEMPLATE_TYPE_LABELS[type] + 'vorlage').join(' und ') }} fehlt
            </div>
          </div>
        </v-col>
        <v-col
          v-if="overlappingConfig"
          cols="12"
        >
          <v-alert
            type="warning"
            dense
            outlined
          >
            {{ overlapText }}
          </v-alert>
        </v-col>
        <v-col
          v-if="error"
          cols="12"
        >
          <v-alert
            type="error"
            dense
          >
            {{ error }}
          </v-alert>
        </v-col>
      </v-row>
    </template>
    <template #dialog-actions>
      <v-btn
        color="primary"
        :disabled="!isValid || saving"
        :loading="saving"
        @click="save"
      >
        Speichern
      </v-btn>
      <v-btn @click="close">
        Schliessen
      </v-btn>
    </template>
  </base-dialog>
</template>

<script>
import BaseDialog from '@/components/UI/BaseDialog.vue'
import { SET_SNACKBAR_MESSAGE } from '@/modules/common/store/mutation-types'
import { formatApiError } from '@/lib/api-errors'
import moment from 'moment'
import { lastValidDay, validityOverlaps, validNotafterFromLastDay } from '@/lib/sort-utils'

const DEFAULT_TAX_EXEMPT_PURPOSE = 'kirchlicher, gemeinnütziger oder mildtätiger Zwecke im Sinne der §§ 52-54 Abgabenordnung (AO):'
const DEFAULT_MIN_AMOUNT = '300'

// "300.000000" -> "300", "12.500000" -> "12.5"; stays a string
const trimDecimal = (value) => {
  if (value === null || value === undefined || value === '') return DEFAULT_MIN_AMOUNT
  const str = `${value}`
  return str.includes('.') ? str.replace(/0+$/, '').replace(/\.$/, '') : str
}

// The backend requires exactly one SINGLE and one BATCH template per configuration
const TEMPLATE_TYPE_LABELS = { single: 'Einzel', batch: 'Sammel' }
// Template files are global and assigned to a set by their name (case-insensitive).
// Generic templates without a set name are the Verein templates.
const TEMPLATE_SETS = [
  { value: 'kirche', label: 'Kirche', nameIncludes: 'Kirche' },
  { value: 'stiftung', label: 'Stiftung priv. Rechts', nameIncludes: 'Stiftung priv' },
  { value: 'verein', label: 'Verein (e. V.)', nameIncludes: 'Verein', includesGeneric: true }
]
const nameMatches = (template, set) => template.name.toLowerCase().includes(set.nameIncludes.toLowerCase())
const isGeneric = template => !TEMPLATE_SETS.some(set => nameMatches(template, set))

const normalizeAmount = (value) => `${value ?? ''}`.trim().replace(',', '.')
// Zero is allowed, empty is not
const isValidAmount = (value) => /^\d+(\.\d+)?$/.test(normalizeAmount(value))

export default {
  name: 'DonationReceiptConfigDialog',
  components: { BaseDialog },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    organization: {
      type: Object,
      required: true
    },
    existingConfig: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'saved'],
  data () {
    return {
      amountRules: [
        v => isValidAmount(v) || 'Bitte einen Betrag ≥ 0 angeben.'
      ],
      form: {
        min_amount_donation: DEFAULT_MIN_AMOUNT,
        min_amount_collection: DEFAULT_MIN_AMOUNT,
        valid_from: new Date().toISOString().slice(0, 10),
        valid_notafter: '',
        tax_exempt_purpose: DEFAULT_TAX_EXEMPT_PURPOSE
      },
      TEMPLATE_TYPE_LABELS,
      templateSet: null,
      // Set the existing config's templates belong to; only a different choice re-sends templates
      initialTemplateSet: null,
      // Full config from the manage endpoint; the org payload lacks id, template_file and tax_exempt_purpose
      resolvedConfig: null,
      allTemplateFiles: [],
      loadingTemplates: false,
      saving: false,
      error: null
    }
  },
  computed: {
    templateSets () {
      return TEMPLATE_SETS.map(set => {
        const templates = this.allTemplateFiles.filter(f => nameMatches(f, set) || (set.includesGeneric && isGeneric(f)))
        // Older uploads of a set stay around; use the newest template of each type
        const newest = Object.keys(TEMPLATE_TYPE_LABELS).map(type => templates
          .filter(t => t.type === type)
          .sort((a, b) => moment(b.created_at).diff(moment(a.created_at)))[0])
        return {
          ...set,
          templates,
          selectedUrls: newest.filter(Boolean).map(t => t.url),
          missing: Object.keys(TEMPLATE_TYPE_LABELS).filter((type, i) => !newest[i]),
          complete: newest.every(Boolean)
        }
      })
    },
    selectedTemplateUrls () {
      return this.templateSets.find(set => set.value === this.templateSet)?.selectedUrls ?? []
    },
    hasExistingTemplates () {
      return !!this.resolvedConfig?.template_file?.length
    },
    isValid () {
      const amountsValid = isValidAmount(this.form.min_amount_donation) && isValidAmount(this.form.min_amount_collection)
      // An existing config may be open-ended at the start; new ones need a start date
      const datesValid = (!!this.form.valid_from || !!this.existingConfig) &&
        (!this.form.valid_notafter || !this.form.valid_from || this.form.valid_notafter >= this.form.valid_from)
      const templatesValid = !!this.templateSet || (!!this.existingConfig && this.hasExistingTemplates)
      return amountsValid && datesValid && templatesValid && !this.overlappingConfig &&
        (!this.existingConfig || !!this.resolvedConfig)
    },
    // The manage endpoint takes plain dates
    validNotafter () {
      return validNotafterFromLastDay(this.form.valid_notafter)?.format('YYYY-MM-DD') ?? null
    },
    // The backend uses the first valid configuration of an organization, so windows must not overlap
    overlappingConfig () {
      const validity = { valid_from: this.form.valid_from || null, valid_notafter: this.validNotafter }
      return (this.organization.donation_receipt_configurations ?? [])
        .filter(config => config !== this.existingConfig)
        .find(config => validityOverlaps(config, validity)) ?? null
    },
    overlapText () {
      const config = this.overlappingConfig
      if (!config) return ''
      const range = [
        config.valid_from ? `ab ${moment(config.valid_from).format('DD.MM.YYYY')}` : '',
        config.valid_notafter ? `bis ${lastValidDay(config.valid_notafter).format('DD.MM.YYYY')}` : ''
      ].filter(Boolean).join(' ') || 'ohne Einschränkung'
      return `Der Gültigkeitszeitraum überschneidet sich mit einer bestehenden Konfiguration (${range}).`
    }
  },
  watch: {
    isOpen (val) {
      if (val) this.init()
    }
  },
  methods: {
    async init () {
      this.error = null
      this.saving = false
      this.templateSet = null
      this.initialTemplateSet = null
      this.resolvedConfig = null
      this.form = {
        min_amount_donation: DEFAULT_MIN_AMOUNT,
        min_amount_collection: DEFAULT_MIN_AMOUNT,
        valid_from: new Date().toISOString().slice(0, 10),
        valid_notafter: '',
        tax_exempt_purpose: DEFAULT_TAX_EXEMPT_PURPOSE
      }

      if (this.existingConfig) {
        // Keep DecimalStrings as strings; a missing amount falls back to the default of 300
        this.form.min_amount_donation = trimDecimal(this.existingConfig.min_amount_donation)
        this.form.min_amount_collection = trimDecimal(this.existingConfig.min_amount_collection)
        this.form.valid_from = this.existingConfig.valid_from ? moment(this.existingConfig.valid_from).format('YYYY-MM-DD') : ''
        // "Gültig bis" is inclusive in the UI, valid_notafter is an exclusive bound
        this.form.valid_notafter = lastValidDay(this.existingConfig.valid_notafter)?.format('YYYY-MM-DD') ?? ''
      }

      await Promise.all([
        this.allTemplateFiles.length ? null : this.loadTemplates(),
        this.existingConfig ? this.resolveExistingConfig() : null
      ])

      if (this.hasExistingTemplates) {
        const urls = this.resolvedConfig.template_file
        const set = this.templateSets.find(set => urls.every(url => set.templates.some(t => t.url === url)))
        this.templateSet = this.initialTemplateSet = set?.value ?? null
      }
    },
    async resolveExistingConfig () {
      try {
        if (this.existingConfig.id) {
          const response = await this.$store.getters.restApi.get(`donation_receipt/manage/configuration/${this.existingConfig.id}/`)
          this.resolvedConfig = response.data
        } else {
          const response = await this.$store.getters.restApi.get('donation_receipt/manage/configuration/')
          const all = response.data.results ?? response.data
          const sameAmount = (a, b) => (a === null || a === undefined) ? (b === null || b === undefined) : trimDecimal(a) === trimDecimal(b)
          const candidates = all.filter(c => c.organization === this.organization.url)
          // The manage endpoint has no validity dates, so amounts are all there is to tell them apart
          const matches = candidates.length === 1
            ? candidates
            : candidates.filter(c =>
              sameAmount(c.min_amount_donation, this.existingConfig.min_amount_donation) &&
              sameAmount(c.min_amount_collection, this.existingConfig.min_amount_collection)
            )
          if (matches.length > 1) {
            // Guessing could overwrite another validity window
            this.error = 'Mehrere Konfigurationen mit gleichen Mindestbeträgen – diese kann hier nicht eindeutig bearbeitet werden. Bitte im Django-Admin bearbeiten.'
            return
          }
          this.resolvedConfig = matches[0] ?? null
        }
        if (!this.resolvedConfig) {
          this.error = 'Bestehende Konfiguration konnte nicht gefunden werden.'
          return
        }
        if (this.resolvedConfig.tax_exempt_purpose) {
          this.form.tax_exempt_purpose = this.resolvedConfig.tax_exempt_purpose
        }
      } catch {
        this.error = 'Bestehende Konfiguration konnte nicht geladen werden.'
      }
    },
    async loadTemplates () {
      this.loadingTemplates = true
      try {
        const response = await this.$store.getters.restApi.get('donation_receipt/manage/template/')
        this.allTemplateFiles = response.data.results ?? response.data
      } catch {
        this.error = 'Vorlagen konnten nicht geladen werden.'
      } finally {
        this.loadingTemplates = false
      }
    },
    async save () {
      this.saving = true
      this.error = null
      try {
        const payload = {
          min_amount_donation: normalizeAmount(this.form.min_amount_donation),
          min_amount_collection: normalizeAmount(this.form.min_amount_collection),
          valid_from: this.form.valid_from || null,
          valid_notafter: this.validNotafter,
          tax_exempt_purpose: this.form.tax_exempt_purpose
        }
        // Keep the attached templates unless another set was chosen (or none are attached yet)
        if (this.templateSet && (this.templateSet !== this.initialTemplateSet || !this.hasExistingTemplates)) {
          payload.version = '2'
          payload.template_file = this.selectedTemplateUrls
        }

        if (this.existingConfig) {
          await this.$store.getters.restApi.patch(`donation_receipt/manage/configuration/${this.resolvedConfig.id}/`, payload)
        } else {
          payload.organization = this.organization.url
          await this.$store.getters.restApi.post('donation_receipt/manage/configuration/', payload)
        }

        this.$store.commit(SET_SNACKBAR_MESSAGE, {
          message: 'Konfiguration erfolgreich gespeichert.',
          color: 'success'
        })
        this.$emit('saved')
        this.close()
      } catch (e) {
        this.error = formatApiError(e)
      } finally {
        this.saving = false
      }
    },
    close () {
      this.$emit('close')
    }
  }
}
</script>
